package chat.amp.tim

import de.connect2x.trixnity.messenger.util.DownloadManager
import de.connect2x.trixnity.messenger.util.FileTransferProgressElement
import de.connect2x.trixnity.messenger.util.IOOrDefault
import de.connect2x.trixnity.messenger.viewmodel.util.formatProgress
import io.github.oshai.kotlinlogging.KotlinLogging
import kotlinx.coroutines.CoroutineExceptionHandler
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Deferred
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.Job
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.async
import kotlinx.coroutines.cancelAndJoin
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.launch
import net.folivo.trixnity.client.MatrixClient
import net.folivo.trixnity.client.media
import net.folivo.trixnity.client.media.PlatformMedia
import net.folivo.trixnity.clientserverapi.model.media.FileTransferProgress
import net.folivo.trixnity.core.model.events.m.room.RoomMessageEventContent
import kotlin.coroutines.CoroutineContext

private val log = KotlinLogging.logger { }

// TODO should have platform implementations in future (Background Job in Android for example)
class ampDownloadManagerImpl(
    coroutineContext: CoroutineContext = Dispatchers.IOOrDefault
) : DownloadManager {
    private val scope =
        CoroutineScope(
            coroutineContext
                    + SupervisorJob(coroutineContext[Job])
                    + CoroutineExceptionHandler { _, throwable -> log.error(throwable) { "DownloadManager failed." }}
        )
    private val _downloads = MutableStateFlow(listOf<Download>())
    // override val downloads: StateFlow<List<Download>> = _downloads.asStateFlow() // TODO for possible DownloadManagerViewModel

    override fun startDownloadAsync(
        matrixClient: MatrixClient,
        content: RoomMessageEventContent.FileBased,
        fileName: String,
        progress: MutableStateFlow<FileTransferProgressElement?>,
    ): Deferred<Result<PlatformMedia>> {
        log.debug { "add $fileName to current downloads" }
        val download = Download(fileName, content.info?.size, progress)
        _downloads.value += download

        val trixnityProgress = MutableStateFlow<FileTransferProgress?>(null)

        return scope.async {
            val progressJob = launch {
                trixnityProgress.collect { trixnityProgress ->
                    if (trixnityProgress != null) {
                        log.trace { "download progress for $fileName: ${trixnityProgress.transferred} / ${trixnityProgress.total}" }
                        val total = trixnityProgress.total
                        if (total != null)
                            progress.value = FileTransferProgressElement(
                                trixnityProgress.transferred.toFloat() / total.toFloat(),
                                formatProgress(trixnityProgress)
                            )
                    }
                }
            }
            val encryptedFile = content.file
            val url = content.url

            // request scanning result
            val scanResult = matrixClient.api.baseClient.request(ScanEncryptedMedia(asUserId = null), ScanEncryptedMedia.Request(encryptedFile))
            log.debug { "scanResult is $scanResult" }

            var virusInfo: String? = null
            scanResult.onSuccess { response ->
                if (!response.clean) {
                    // ***VIRUS DETECTED***
                    log.debug { response.info }
                    virusInfo = response.info
                }
            }.onFailure { throwable ->
                virusInfo = throwable.message
            }

            val result =
                when {
                    virusInfo != null -> Result.failure(Error(virusInfo))
                    encryptedFile != null -> matrixClient.media.getEncryptedMedia(encryptedFile, trixnityProgress)
                    url != null -> matrixClient.media.getMedia(url, trixnityProgress)
                    else -> Result.failure(IllegalArgumentException("there was no url or file in content"))
                }.onSuccess {
                    log.debug { "successfully downloaded $fileName" }
                }.onFailure {
                    log.warn(it) { "download for $fileName was not successful" }
                }
            progressJob.cancelAndJoin()
            _downloads.value -= download // we remove Download history for now
            result
        }
    }
}

data class Download(
    val fileName: String,
    val fileSize: Long?,
    val progress: StateFlow<FileTransferProgressElement?>,
)
